/**
 * Schedule Layout Algorithm
 *
 * Greedy column-packing for overlapping time-slot items.
 * Assigns each item a column index and total column count so they
 * can be rendered side-by-side when they overlap in time.
 */

export interface ScheduleItem {
  id: string;
  title: string;
  startTime: number; // ms timestamp
  endTime: number;   // ms timestamp
  isAllDay: boolean;
  type: 'task' | 'calendar_event';
  color: string | null;
  isReadOnly: boolean;
  taskData?: unknown;
  eventData?: unknown;
}

export interface PositionedItem extends ScheduleItem {
  column: number;       // 0-based column index
  totalColumns: number; // total columns in overlap group
  top: number;          // px offset from grid top
  height: number;       // px height
}

/**
 * Calculate overlap layout for a list of schedule items.
 *
 * Items are sorted by start time, then by duration (longest first).
 * A greedy algorithm assigns items to the first available column.
 * Connected overlapping groups share the same totalColumns value.
 *
 * @param items - Array of schedule items (non all-day)
 * @param dayStart - Start of the day in ms (e.g. 07:00 of the target day)
 * @param pixelsPerMs - Conversion factor from ms to pixels
 * @param minHeightPx - Minimum height in pixels for any item
 * @returns Array of items with positioning metadata
 */
export function calculateOverlapLayout(
  items: ScheduleItem[],
  dayStart: number,
  pixelsPerMs: number,
  minHeightPx = 24
): PositionedItem[] {
  if (items.length === 0) return [];

  // Filter out all-day items and sort by start time, then by duration (longest first)
  const timedItems = items
    .filter((item) => !item.isAllDay)
    .sort((a, b) => {
      const startDiff = a.startTime - b.startTime;
      if (startDiff !== 0) return startDiff;
      // Longer items first when same start time
      return (b.endTime - b.startTime) - (a.endTime - a.startTime);
    });

  if (timedItems.length === 0) return [];

  // Build overlap groups (connected components)
  const groups: ScheduleItem[][] = [];
  let currentGroup: ScheduleItem[] = [timedItems[0]];
  let groupEnd = timedItems[0].endTime;

  for (let i = 1; i < timedItems.length; i++) {
    const item = timedItems[i];
    if (item.startTime < groupEnd) {
      // Overlaps with current group
      currentGroup.push(item);
      groupEnd = Math.max(groupEnd, item.endTime);
    } else {
      // New group
      groups.push(currentGroup);
      currentGroup = [item];
      groupEnd = item.endTime;
    }
  }
  groups.push(currentGroup);

  // Assign columns within each group
  const result: PositionedItem[] = [];

  for (const group of groups) {
    // columnEnds[col] = end time of last item assigned to that column
    const columnEnds: number[] = [];

    const assignments: { item: ScheduleItem; column: number }[] = [];

    for (const item of group) {
      // Find first column where item doesn't overlap
      let assignedCol = -1;
      for (let col = 0; col < columnEnds.length; col++) {
        if (item.startTime >= columnEnds[col]) {
          assignedCol = col;
          break;
        }
      }

      if (assignedCol === -1) {
        // Need a new column
        assignedCol = columnEnds.length;
        columnEnds.push(0);
      }

      columnEnds[assignedCol] = item.endTime;
      assignments.push({ item, column: assignedCol });
    }

    const totalColumns = columnEnds.length;

    for (const { item, column } of assignments) {
      const top = (item.startTime - dayStart) * pixelsPerMs;
      const height = Math.max(
        (item.endTime - item.startTime) * pixelsPerMs,
        minHeightPx
      );

      result.push({
        ...item,
        column,
        totalColumns,
        top,
        height,
      });
    }
  }

  return result;
}

/**
 * Convert a timestamp to Y pixel position relative to grid top.
 */
export function timeToY(timestamp: number, dayStart: number, pixelsPerMs: number): number {
  return (timestamp - dayStart) * pixelsPerMs;
}

/**
 * Convert a Y pixel position to timestamp.
 */
export function yToTime(y: number, dayStart: number, pixelsPerMs: number): number {
  return dayStart + y / pixelsPerMs;
}

/**
 * Snap a timestamp to the nearest slot boundary.
 * @param timestamp - ms timestamp
 * @param slotDurationMs - slot size in ms (e.g. 15*60*1000 for 15 min)
 */
export function snapToSlot(timestamp: number, slotDurationMs: number): number {
  return Math.round(timestamp / slotDurationMs) * slotDurationMs;
}

/** Time grid constants */
export const SLOT_HEIGHT_PX = 48; // pixels per 30-minute slot
export const SLOT_DURATION_MS = 30 * 60 * 1000; // 30 minutes in ms
export const DEFAULT_START_HOUR = 7;
export const DEFAULT_END_HOUR = 22;
export const PIXELS_PER_MS = SLOT_HEIGHT_PX / SLOT_DURATION_MS;
